import { Component, Inject, OnDestroy, OnInit } from "@angular/core"
import { CommonModule } from "@angular/common"
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog"
import { MatButtonModule } from "@angular/material/button"
import { MatIconModule } from "@angular/material/icon"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import { Subscription } from "rxjs"
import { isAiredDelayed, StationScheduleService } from "../../services/station-schedule.service"
import { MatTooltipModule } from "@angular/material/tooltip"
import { getCustomerDisplay, OrderCustomerDisplay } from "../../utils/order-display-amount"

export interface ScheduleDetailDialogData {
  scheduleId: number
  /** Optional list-row fields so creator can show before/without relying only on by-id payload */
  customer_firstname?: string | null
  customer_lastname?: string | null
  customer_accounttype?: string | null
  customer_businessname?: string | null
}

@Component({
  selector: "app-schedule-detail-dialog",
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule],
  template: `
    <div class="schedule-detail-dialog" (click)="$event.stopPropagation()">
      <div class="detail-header">
        <h2 class="detail-title">Schedule Details</h2>
        <button
          mat-icon-button
          type="button"
          class="detail-close-btn"
          (click)="close($event)"
          aria-label="Close schedule details"
        >
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <mat-dialog-content class="detail-content">
        @if (loading) {
          <div class="detail-state detail-loading" aria-live="polite" aria-busy="true">
            <mat-spinner diameter="36"></mat-spinner>
            <span>Loading schedule details…</span>
          </div>
        } @else if (detailData) {
          <div class="detail-body">
            @if (detailAudioUrl()) {
              <div class="detail-audio">
                <audio [src]="detailAudioUrl()!" controls preload="metadata"></audio>
              </div>
            }
            <div class="detail-grid">
              <div><strong>ID</strong><span>{{ detailData.id }}</span></div>
              <div><strong>Station</strong><span>{{ detailData.station_call_sign || detailData.stationid }}</span></div>
              <div><strong>Scheduled</strong><span>{{ detailData.scheduledtime ? (detailData.scheduledtime | date:'medium') : '-' }}</span></div>
              <div>
                <strong>Aired</strong>
                <span class="aired-value">
                  {{ detailData.airedtimestamp ? (detailData.airedtimestamp | date:'medium') : '-' }}
                  @if (isDelayedAir()) {
                    <span
                      class="badge badge-aired-delayed"
                      matTooltip="Aired outside scheduled window (±30 min)"
                      matTooltipPosition="above">
                      Delayed air
                    </span>
                  }
                </span>
              </div>
              <div><strong>Recording</strong><span>{{ detailData.recording_name || detailData.recordingid || '-' }}</span></div>
              <div><strong>Talent</strong><span>{{ detailData.talent_name || '-' }}</span></div>
              <div><strong>Length</strong><span>{{ detailData.recording_length || detailData.audiolength || '-' }}s</span></div>
              <div><strong>Order</strong><span>{{ detailData.orderid || '-' }}</span></div>
              @if (creator(); as customer) {
                <div><strong>{{ customer.label }}</strong><span>{{ customer.value }}</span></div>
              }
              @if (detailData.order_start_date || detailData.order_end_date) {
                <div><strong>Order Dates</strong><span>{{ detailData.order_start_date | date:'mediumDate'}} - {{ detailData.order_end_date | date:'mediumDate' }}</span></div>
              }
              <div><strong>Impressions</strong><span>{{ detailData.impressions || '-' }}</span></div>
              <div><strong>Ratecard</strong><span>{{ detailData.ratecard_value || '-' }}</span></div>
              <div><strong>CPM</strong><span>{{ detailData.overall_cpm || detailData.CPM_value || '-' }}</span></div>
              <div><strong>Status</strong><span>{{ detailData.recording_status ?? '-' }}</span></div>
            </div>
          </div>
        } @else {
          <div class="detail-state detail-error" aria-live="polite">
            Unable to load schedule details.
          </div>
        }
      </mat-dialog-content>
    </div>
  `,
  styles: [
    `
      .schedule-detail-dialog {
        box-sizing: border-box;
        padding: 20px 24px 24px;
        color: var(--text-primary);
      }

      .detail-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-bottom: 16px;
      }

      .detail-header h2,
      .detail-title {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 600;
        color: var(--text-primary);
      }

      .detail-close-btn {
        color: var(--text-primary);
      }

      .detail-close-btn mat-icon {
        color: var(--text-primary);
      }

      :host ::ng-deep mat-dialog-content.detail-content.mat-mdc-dialog-content {
        padding: 0 !important;
        margin: 0;
        max-height: none !important;
        min-height: 180px;
      }

      .detail-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 14px;
        min-height: 180px;
        padding: 8px 0 16px;
        color: var(--text-secondary);
        text-align: center;
      }

      .detail-body {
        animation: detail-fade-in 0.22s ease;
      }

      .detail-audio {
        margin-bottom: 12px;
      }

      .detail-audio audio {
        width: 100%;
        max-width: 480px;
      }

      .detail-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 16px 20px;
      }

      .detail-grid > div {
        display: grid;
        gap: 4px;
      }

      .detail-grid strong {
        color: var(--text-primary);
      }

      .detail-grid span {
        color: var(--text-secondary);
      }

      .aired-value {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 0.45rem;
      }

      .badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: fit-content;
        padding: 0.2rem 0.55rem;
        border-radius: 999px;
        font-size: 0.68rem;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        line-height: 1.2;
        white-space: nowrap;
      }

      .badge-aired-delayed {
        background: #f97316;
        color: #fff;
      }

      @keyframes detail-fade-in {
        from {
          opacity: 0;
          transform: translateY(4px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `,
  ],
})
export class ScheduleDetailDialogComponent implements OnInit, OnDestroy {
  loading = true
  detailData: any = null
  private detailSub: Subscription | null = null
  private closing = false

  constructor(
    private dialogRef: MatDialogRef<ScheduleDetailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private data: ScheduleDetailDialogData,
    private scheduleSvc: StationScheduleService,
  ) {}

  ngOnInit(): void {
    this.detailSub = this.scheduleSvc.byId(this.data.scheduleId).subscribe({
      next: (res) => {
        this.detailData = {
          ...res.schedule,
          customer_firstname: res.schedule?.customer_firstname ?? this.data.customer_firstname,
          customer_lastname: res.schedule?.customer_lastname ?? this.data.customer_lastname,
          customer_accounttype: res.schedule?.customer_accounttype ?? this.data.customer_accounttype,
          customer_businessname: res.schedule?.customer_businessname ?? this.data.customer_businessname,
        }
        this.loading = false
      },
      error: () => {
        this.loading = false
      },
    })
  }

  ngOnDestroy(): void {
    this.detailSub?.unsubscribe()
  }

  creator(): OrderCustomerDisplay | null {
    return getCustomerDisplay(this.detailData || this.data)
  }

  detailAudioUrl(): string | null {
    return this.detailData?.audio_url ?? null
  }

  isDelayedAir(): boolean {
    return isAiredDelayed(this.detailData)
  }

  close(event?: Event): void {
    event?.stopPropagation()
    if (this.closing) return
    this.closing = true
    this.dialogRef.close()
    setTimeout(() => {
      this.closing = false
    }, 300)
  }
}
